import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import SectionHeading from '../components/ui/SectionHeading';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiStar, FiArrowRight } from 'react-icons/fi';
import { servicesList, reviews } from '../data/content';

const Home = () => {
  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-veloura-bg overflow-hidden">
        <div className="absolute inset-0 z-0 w-full lg:w-1/2 right-0 ml-auto hidden lg:block">
          <img 
            src="https://plus.unsplash.com/premium_photo-1661768055500-45bf4357bb85?auto=format&fit=crop&q=80&w=1200" 
            alt="Luxury lash extensions" 
            className="w-full h-full object-cover object-center rounded-l-[100px]"
          />
        </div>
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="max-w-2xl bg-white/80 backdrop-blur-md lg:bg-transparent lg:backdrop-blur-none p-8 lg:p-0 rounded-2xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <span className="text-veloura-accent uppercase tracking-[0.2em] text-sm font-semibold mb-4 block">Montreal's Premier Studio</span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-veloura-text leading-tight mb-6">
                Luxury Lash <br/><span className="italic font-light text-veloura-accent">Extensions</span> <br/>Designed Around You.
              </h1>
              <p className="text-lg text-veloura-muted mb-10 max-w-xl leading-relaxed">
                Montreal lash studio specializing in natural, hybrid, volume, and mega volume lash extensions with personalized service and effortless online booking.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button to="/book" variant="primary">Book Appointment</Button>
                <Button to="/services" variant="outline">View Services</Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-y border-veloura-primary/20">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-center">
            {[
              { label: 'Certified Artists', text: 'Highly trained professionals' },
              { label: 'Premium Products', text: 'Safe, hypoallergenic materials' },
              { label: 'Custom Styling', text: 'Tailored to your eye shape' }
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-veloura-accent mb-3" />
                <h3 className="font-serif text-lg text-veloura-text">{item.label}</h3>
                <p className="text-sm text-veloura-muted">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-veloura-bg">
        <div className="container mx-auto px-6 max-w-7xl">
          <SectionHeading title="Signature Services" subtitle="Enhance Your Beauty" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesList.slice(0, 3).map((service, index) => (
              <motion.div 
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 group"
              >
                <div className="h-64 overflow-hidden">
                  <img src={service.img} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl mb-2">{service.title}</h3>
                  <p className="text-veloura-accent font-medium mb-4">{service.price}</p>
                  <p className="text-veloura-muted text-sm mb-6 line-clamp-2">{service.desc}</p>
                  <Link to="/services" className="inline-flex items-center text-sm font-semibold uppercase tracking-wider text-veloura-text hover:text-veloura-accent transition-colors">
                    Learn More <SafeIcon icon={FiArrowRight} className="ml-2" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button to="/services" variant="secondary">View All Services</Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us & Intro */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <div className="relative">
                <img src="https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&q=80&w=800" alt="Studio interior" className="rounded-[40px] shadow-2xl relative z-10" />
                <div className="absolute -bottom-8 -right-8 w-full h-full border-2 border-veloura-primary rounded-[40px] z-0"></div>
              </div>
            </div>
            
            <div className="lg:w-1/2">
              <SectionHeading title="The Veloura Experience" subtitle="Why Choose Us" centered={false} />
              <p className="text-veloura-muted mb-6 text-lg leading-relaxed">
                Located in the heart of downtown Montreal on vibrant Rue Crescent, Veloura Lash Studio offers a sanctuary for those seeking impeccable lash artistry.
              </p>
              <p className="text-veloura-muted mb-8 text-lg leading-relaxed">
                Our approach is hyper-personalized. We don't believe in one-size-fits-all. We consider your natural lash health, eye shape, and lifestyle to map a bespoke set that enhances your unique features while maintaining strict hygiene and safety protocols.
              </p>
              <ul className="space-y-4 mb-10">
                {['Meticulous isolation technique', 'Strict sanitation standards', 'Premium weightless lashes', 'Relaxing spa environment'].map((item, i) => (
                  <li key={i} className="flex items-center text-veloura-text">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-veloura-accent mr-3" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button to="/about" variant="outline">Meet The Founder</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 bg-veloura-primary/20">
        <div className="container mx-auto px-6 max-w-7xl">
          <SectionHeading title="Client Love" subtitle="Testimonials" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm relative"
              >
                <div className="flex gap-1 text-yellow-400 mb-4">
                  {[...Array(review.rating)].map((_, j) => <SafeIcon key={j} icon={FiStar} className="fill-current" />)}
                </div>
                <p className="text-veloura-muted italic mb-6">"{review.text}"</p>
                <p className="font-serif font-semibold text-veloura-text">- {review.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-veloura-accent text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1512496015851-a1c825b27264?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center"></div>
        <div className="container mx-auto px-6 relative z-10">
          <h2 className="text-4xl md:text-5xl font-serif mb-6">Ready for Flawless Lashes?</h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Secure your spot at Montreal's most sought-after lash studio. Appointments fill up quickly.
          </p>
          <Button to="/book" variant="secondary" className="!bg-white !text-veloura-accent hover:!bg-veloura-bg px-10 py-4 text-lg">
            This is a Demo Site - Bookings Disabled
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;